import axios from 'axios'
import * as endPoints from '@/endpoints'
import { getCookie } from '@/features/allauth/context/AllauthAuthContext'
import { mfaSetupRedirect } from '@/features/allauth/lib/mfaPolicy'

const axiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || ''
})

const getBrowserTimezone = () =>
    Intl.DateTimeFormat().resolvedOptions().timeZone || null

axiosInstance.defaults.xsrfHeaderName = 'X-CSRFToken'
axiosInstance.defaults.xsrfCookieName = 'csrftoken'
axiosInstance.defaults.withCredentials = true
axiosInstance.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

axiosInstance.interceptors.request.use((config) => {
    const timezone = getBrowserTimezone()
    if (!timezone) return config

    config.headers = config.headers || {}
    const hasTimezoneHeader =
        Boolean(config.headers['X-Timezone']) ||
        Boolean(config.headers['x-timezone']) ||
        Boolean(config.headers['Time-Zone']) ||
        Boolean(config.headers['time-zone'])

    if (!hasTimezoneHeader) {
        config.headers['X-Timezone'] = timezone
    }
    return config
})

axiosInstance.interceptors.response.use(
    (response) =>
        // Any status code that lie within the range of 2xx cause this function to trigger
        response,
    (error) => {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        if (error.response) {
            if (
                error.response.status === 403 &&
                error.response.headers['X-CSRF-Error']
            ) {
                return axiosInstance
                    .get(endPoints.ACCOUNT_SET_CSRF)
                    .then(() => {
                        const token = getCookie('csrftoken')
                        if (token) {
                            axiosInstance.defaults.headers.common[
                                'X-CSRFToken'
                            ] = token
                        }
                        return axiosInstance.request(error.config)
                    })
                    .catch((refreshError) => Promise.reject(refreshError))
            }
        }

        // An administrator who signed in with a password and has no authenticator
        // app is refused by the backend until they set one up. MfaSetupGuard
        // catches this at sign-in; this catches it mid-session (the authenticator
        // was removed, or the requirement was switched on).
        const mfaRedirect = mfaSetupRedirect(
            error.response,
            window.location.pathname
        )
        if (mfaRedirect) {
            window.location.assign(mfaRedirect)
        }

        // If the error is not handled above, just reject the promise with the error
        return Promise.reject(error)
    }
)

export default axiosInstance
